import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'

import { BookkeepingItem } from '@features/reconciliation/types/bookkeepingItem'
import { fetchContacts } from '@modules-deprecated/app/contacts/query-api'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { Posting } from '@modules-deprecated/bankReconciliation/types'

import { defaultCacheTime, defaultStaleTime } from '../../../config/queryClient'
import { QueryKeys } from '../../../enums/queryKeys'
import { BankLineSubjectAssociationWithSubject } from '../types/bankLineSubjectAssociationWithSubject'
import { resolveMatchedItems } from '../utils/resolveMatchedItems'

type PostingAssociation = BankLineSubjectAssociationWithSubject & { subject: Posting }

const isPostingAssociation = (association: BankLineSubjectAssociationWithSubject): association is PostingAssociation =>
  'transactionId' in association.subject

interface UseResolveMatchedItemsParams {
  associations: BankLineSubjectAssociationWithSubject[]
  matchId?: string
  bankLineId?: string
  bankLineAmount?: number
  enabled?: boolean
}

interface UseResolveMatchedItemsResult {
  items: BookkeepingItem[]
  isLoading: boolean
}

export const useResolveMatchedItems = ({
  associations,
  matchId,
  bankLineId,
  bankLineAmount,
  enabled = true,
}: UseResolveMatchedItemsParams): UseResolveMatchedItemsResult => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const organizationId = organization?.id

  const { data: contactsData, isLoading: isContactsLoading } = useQuery(
    [QueryKeys.Contacts, organizationId],
    () => fetchContacts({ organizationId: organizationId as string }),
    {
      enabled: enabled && !!organizationId,
      staleTime: defaultCacheTime,
    },
  )

  const contactsMap = useMemo(() => {
    const contacts = contactsData?.contacts || []

    return new Map(contacts.flatMap((contact) => (contact.id && contact.name ? [[contact.id, contact.name]] : [])))
  }, [contactsData])

  const postingAssociations = useMemo(
    () =>
      associations
        .filter(isPostingAssociation)
        .filter((a) => bankLineAmount === undefined || a.subject.amount === bankLineAmount),
    [associations, bankLineAmount],
  )

  // Wait for contacts to settle (success or failure) rather than requiring data, so a failed
  // contacts fetch degrades to missing names instead of hiding the whole matched section.
  const isEnabled = enabled && !!matchId && postingAssociations.length > 0 && !isContactsLoading

  const { data, isLoading } = useQuery(
    [QueryKeys.BankLineMatchedItems, matchId, bankLineId],
    () =>
      resolveMatchedItems(
        postingAssociations.map((association) => association.subject),
        contactsMap,
        t,
      ),
    {
      enabled: isEnabled,
      staleTime: defaultStaleTime,
    },
  )

  return useMemo(
    () => ({
      items: data ?? [],
      isLoading: isContactsLoading || isLoading,
    }),
    [data, isContactsLoading, isLoading],
  )
}
